<template>
    <div>
        <!-- Static Toolbar -->
        <div class="position-fixed zoom-btns mr-3">
            <div>
                <div class="btn-group-vertical shadow rounded mb-2" role="group" aria-label="Basic example">
                    <button
                        type="button"
                        class="btn btn-gray tool-btn"
                        :disabled="decreaseZoomDisabled"
                        @click="decreaseZoom()"
                        aria-label="Decrease Zoom"
                    >
                        <span class="h5">-</span>
                    </button>
                    <button
                        type="button"
                        class="btn btn-gray tool-btn"
                        :disabled="increaseZoomDisabled"
                        @click="increaseZoom()"
                        aria-label="Increase Zoom"
                    >
                        <span class="h5">+</span>
                    </button>
                </div>
            </div>
            <div>
                <div
                    class="btn-group-vertical btn-group-toggle shadow rounded mb-2"
                    role="group"
                    aria-label="Map type toggle button group"
                >
                    <label
                        class="btn btn-gray tool-btn p-2"
                        :class="{ active: mapViewType == 'grid' }"
                        :disabled="loading"
                    >
                        <input
                            type="radio"
                            name="options"
                            aria-label="Switch to grid view"
                            @click="openMapView('grid')"
                        />
                        <img src="/static/general/img/icons/map-grid.svg" alt="Grid-structure icon" height="19" />
                    </label>
                    <label class="btn btn-gray tool-btn p-2" :class="{ active: mapViewType == 'tree' }">
                        <input
                            type="radio"
                            name="options"
                            aria-label="Switch to tree view"
                            @click="openMapView('tree')"
                            :disabled="loading"
                        />
                        <img src="/static/general/img/icons/map-tree.svg" height="20" alt="Tree-structure icon" />
                    </label>
                </div>
            </div>
            <div>
                <button class="btn btn-primary tool-btn p-2 shadow" @click="openCreateNode()" type="button">
                    <span class="icon icon-glyphicons-basic-plus align-middle"></span>
                </button>
            </div>
        </div>

        <!-- Map Container -->
        <div class="map-parent">
            <div class="container-fluid map-container" @click="closeEditor($event)">
                <div class="row">
                    <div class="col-12 d-none d-md-block">
                        <br />
                        <br />
                        <div>
                            <div id="map-container" class="d-flex justify-content-center">
                                <!-- Grid View -->
                                <div
                                    id="grid-container"
                                    class="d-flex flex-column align-items-center px-4"
                                    v-if="mapViewType == 'grid'"
                                >
                                    <div
                                        v-for="(nodeLevel, index) in nodeList"
                                        :key="index"
                                        class="d-flex align-items-center"
                                    >
                                        <div class="d-flex">
                                            <div
                                                v-for="nodeObj in nodeLevel"
                                                :key="nodeObj.id"
                                                :id="'map-node-' + nodeObj.id"
                                                @click.stop="openNodeDetail(nodeObj)"
                                                @mouseover="mouseOverNode(nodeObj)"
                                                @mouseleave="mouseLeaveNode(nodeObj)"
                                                class="bg-white rounded-lg d-flex justify-content-center align-items-center node-block"
                                                :class="{
                                                    'focussed-node': mapSelectedNode.id == nodeObj.id,
                                                    'border-default': !(nodeObj.end_node || nodeObj.completion_node),
                                                    'border-red': nodeObj.end_node,
                                                    'border-green': nodeObj.completion_node,
                                                }"
                                                :style="currentZoomNodeStylesFormatted"
                                            >
                                                <p class="text-center mb-0 node-label">
                                                    {{ nodeObj.number }}. {{ nodeObj.title }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <!-- Tree View -->
                                <div id="tree-container" class="px-4" v-if="mapViewType == 'tree'">
                                    <!-- Connected Nodes (tree) -->
                                    <node-map-branch
                                        :node="nodeList[0]"
                                        :node-styles="currentZoomNodeStylesFormatted"
                                        v-on:open-node-detail="openNodeDetail($event)"
                                        v-on:mouse-over-node="mouseOverNode($event)"
                                        v-on:mouse-leave-node="mouseLeaveNode($event)"
                                    ></node-map-branch>
                                    <!-- Unattached (floating) Nodes -->
                                    <node-map-branch
                                        v-for="node in nodeMap.unattached_nodes"
                                        :key="node.id"
                                        :node="node"
                                        :node-styles="currentZoomNodeStylesFormatted"
                                        v-on:open-node-detail="openNodeDetail($event)"
                                        v-on:mouse-over-node="mouseOverNode($event)"
                                        v-on:mouse-leave-node="mouseLeaveNode($event)"
                                    ></node-map-branch>
                                </div>
                                <!-- SVG Connector Lines -->
                                <div
                                    id="map-svg-container"
                                    class="svg-container line-container"
                                    v-show="!switchingView"
                                >
                                    <svg
                                        id="map-svg"
                                        class="svg-parent"
                                        xmlns="http://www.w3.org/2000/svg"
                                        v-if="svgRendered"
                                    >
                                        <defs>
                                            <marker
                                                id="arrow-selected"
                                                viewBox="0 0 10 10"
                                                refX="6.5"
                                                refY="5"
                                                :markerWidth="zoomedSelectedArrowWidth"
                                                :markerHeight="zoomedSelectedArrowWidth"
                                                orient="auto-start-reverse"
                                                markerUnits="userSpaceOnUse"
                                            >
                                                <path d="M 0 0 L 8 5 L 0 10 z" class="svg-arrow-selected" />
                                            </marker>
                                            <marker
                                                id="arrow-forward"
                                                viewBox="0 0 10 10"
                                                refX="6.5"
                                                refY="5"
                                                :markerWidth="zoomedArrowWidth"
                                                :markerHeight="zoomedArrowWidth"
                                                orient="auto-start-reverse"
                                                markerUnits="userSpaceOnUse"
                                            >
                                                <path
                                                    d="M 0 0 L 8 5 L 0 10 z"
                                                    class="svg-arrow-forward"
                                                    :class="{ 'svg-element-faded': showEditor }"
                                                />
                                            </marker>
                                            <marker
                                                id="arrow-loopback"
                                                viewBox="0 0 10 10"
                                                refX="6.5"
                                                refY="5"
                                                :markerWidth="zoomedArrowWidth"
                                                :markerHeight="zoomedArrowWidth"
                                                orient="auto-start-reverse"
                                                markerUnits="userSpaceOnUse"
                                            >
                                                <path
                                                    d="M 0 0 L 8 5 L 0 10 z"
                                                    class="svg-arrow-loopback"
                                                    :class="{ 'svg-element-faded': showEditor }"
                                                />
                                            </marker>
                                        </defs>
                                        <polyline
                                            v-for="(polyline, index) in connectionPolylines"
                                            :id="'line-' + polyline.startNodeId + 'to' + polyline.endNodeId"
                                            :key="index"
                                            :points="polyline.str"
                                            :stroke-dasharray="polyline.dashedArr"
                                            class="svg-line"
                                            :class="{
                                                'svg-line-selected': polyline.focus,
                                                'svg-forward': !polyline.loopBack,
                                                'svg-loopback': polyline.loopBack,
                                                'svg-element-faded': showEditor,
                                            }"
                                            :style="{
                                                'stroke-width': getZoomedLineWidth(polyline) + 'px',
                                            }"
                                            stroke-linecap="round"
                                            :marker-end="polyline.focus ? 'url(#arrow-selected)' : polyline.arrow"
                                        />
                                    </svg>
                                </div>
                                <!-- SVG Connection Points -->
                                <div class="svg-container point-container" v-show="!switchingView">
                                    <svg class="svg-parent" xmlns="http://www.w3.org/2000/svg" v-if="svgRendered">
                                        <circle
                                            v-for="(marker, index) in connectionStartMarkers"
                                            :key="index"
                                            :cx="marker.x"
                                            :cy="marker.y"
                                            :r="getZoomedMarkerRadius(marker) + 'px'"
                                            class="svg-connection-point"
                                            :class="{
                                                'svg-connection-point-loopback': marker.loopback && !marker.focus,
                                                'svg-connection-point-forward': !marker.loopback && !marker.focus,
                                                'svg-connection-point-focus': marker.focus,
                                                'svg-element-faded': showEditor,
                                            }"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 d-md-none">
                        <br />
                        <br />
                        <p class="h5 text-secondary text-center">Map view isn't available on small screens yet.</p>
                    </div>
                </div>
            </div>

            <!-- Node Editor Sidebar -->
            <div id="editor-menu" class="left-sidebar bg-white shadow" :class="{ 'd-none': !showEditor }">
                <div class="left-sidebar-content d-flex flex-column">
                    <div class="flex-grow-1">
                        <div class="d-flex align-items-center left-sidebar-content-block">
                            <div class="flex-grow-1 pr-2 d-flex align-items-center">
                                <div class="flex-grow-0 mr-2">
                                    <h5 class="mb-0">Screen Details</h5>
                                </div>
                                <div
                                    v-show="editorLoading"
                                    class="spinner-border spinner-border-sm text-primary"
                                    role="status"
                                >
                                    <span class="sr-only">Loading...</span>
                                </div>
                            </div>
                            <div class="flex-grow-0">
                                <div @click="closeEditor()">
                                    <button type="button" class="close" aria-label="Close">
                                        <span aria-hidden="true">&times;</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <hr class="my-0" />
                        <div class="left-sidebar-content-block">
                            <!-- Basic Node Settings -->
                            <div class="mb-3">
                                <div class="d-flex align-items-center justify-content-between">
                                    <router-link v-if="selectedNode.id" :to="selectedNodeRoute">
                                        <span
                                            class="icon icon-lg icon-glyphicons-basic-cogwheel icon-mr align-middle"
                                        ></span>
                                        <span class="align-middle">View All Settings</span>
                                    </router-link>
                                    <button
                                        v-if="selectedNode.id"
                                        @click="previewNode()"
                                        class="btn btn-sm btn-primary"
                                        :disabled="loading"
                                    >
                                        <span class="icon icon-glyphicons-basic-video-play icon-mr"></span>
                                        <span>Preview & Edit</span>
                                    </button>
                                </div>
                            </div>
                            <base-form
                                v-model="selectedNode"
                                :fields="editorFields"
                                v-on:edit="nodeFormEditCb($event)"
                                ref="editor"
                            ></base-form>
                            <node-select
                                v-model="selectedNode.continue_node"
                                :node-options="nodeOptions"
                                :disabled="!selectedNode.continue_button"
                                placeholder="Select or create a target screen"
                                ref="nodeSelector"
                            ></node-select>
                            <div class="d-flex">
                                <div class="flex-grow-1 flex-shrink-0">
                                    <v-select
                                        v-show="selectedNode.continue_button && selectedNode.continue_node"
                                        v-model="selectedNode.correct_answers_required"
                                        :options="continueRequirementOptions"
                                        :close-on-select="true"
                                        label="label"
                                        class="mw-100"
                                        :clearable="false"
                                        :reduce="(option) => option.value"
                                    >
                                        <template slot="option" slot-scope="option">
                                            <span
                                                class="badge badge-light badge-requirement"
                                                style="white-space: pre-wrap"
                                            >
                                                {{ option.label }}
                                            </span>
                                        </template>
                                        <template v-slot:selected-option="option">
                                            <span class="badge badge-light">{{ option.label }}</span>
                                        </template>
                                    </v-select>
                                </div>
                                <div
                                    v-show="selectedNode.correct_answers_required === 'custom'"
                                    class="flex-grow-0 ml-1"
                                >
                                    <input
                                        v-model="selectedNode.correct_answers_required_num"
                                        class="form-control"
                                        type="number"
                                        placeholder="0"
                                        style="max-width: 70px"
                                    />
                                </div>
                            </div>
                            <div class="d-flex justify-content-end pb-3 mt-3">
                                <button @click="saveNode()" :disabled="saveNodeDisabled" class="btn btn-primary btn-sm">
                                    <span>Save</span>
                                    <span v-show="saveLoading" class="ml-2">
                                        <span
                                            class="spinner-border spinner-border-sm"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        <span class="sr-only">Loading...</span>
                                    </span>
                                </button>
                            </div>
                            <!-- Node Contents -->
                            <div class="mt-4 mb-4">
                                <div class="d-flex align-items-center mb-2">
                                    <h3 class="mr-2 h6 mb-0">Content Preview</h3>
                                    <div
                                        v-show="editorLoading"
                                        class="spinner-border spinner-border-sm text-primary"
                                        role="status"
                                    >
                                        <span class="sr-only">Loading...</span>
                                    </div>
                                </div>
                                <p class="text-secondary small mb-2">A quick view of this screen's content.</p>
                                <hr class="mt-2" />
                                <div class="panel-container bg-light rounded">
                                    <div class="d-flex justify-content-center position-relative node-preview-container">
                                        <node-content-preview
                                            :node="selectedNode"
                                            :show-headers="false"
                                            :tiny-mode="true"
                                            v-on:edit="openEditPanel($event)"
                                        ></node-content-preview>
                                    </div>
                                    <div class="d-flex justify-content-end pt-1">
                                        <a
                                            @click="previewNode()"
                                            role="button"
                                            :class="{ disabled: editorLoading }"
                                            class="btn btn-sm btn-link"
                                        >
                                            <span
                                                class="icon icon-100 icon-glyphicons-basic-video-play icon-mr align-middle"
                                            ></span>
                                            <span class="align-middle">Edit Screen</span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="flex-grow-0">
                        <div class="left-sidebar-content-block">
                            <div class="d-flex">
                                <div class="flex-grow-1"></div>
                                <div class="flex-grow-0">
                                    <button
                                        @click="openDeleteNode()"
                                        :disabled="saveNodeDisabled"
                                        class="btn btn-gray btn-sm"
                                    >
                                        <span class="icon icon-glyphicons-basic-bin pr-1"></span>Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Delete Node Modal -->
        <base-modal id="delete-node-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Delete Screen </template>
            <template>
                <p>
                    Delete screen
                    <strong>{{ selectedNode.number }} {{ selectedNode.title }}</strong>
                    ?
                </p>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="deleteNode()" :disabled="modalLoading" class="btn btn-danger" type="button">
                    Delete
                </button>
            </template>
        </base-modal>

        <!-- Panel Editor -->
        <panel-editor
            ref="panelEditor"
            v-on:updated="panelUpdatedCb()"
            :node="selectedNode"
            :case-obj="caseObj"
            :preserve-map="true"
        ></panel-editor>

        <!-- Create Node Modal -->
        <base-modal id="create-node-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> New Screen </template>
            <template>
                <base-form
                    class="mt-2"
                    v-model="newNode"
                    :fields="newNodeFields"
                    :inline-style="true"
                    :condensed-style="true"
                    ref="createNodeForm"
                ></base-form>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="createNode()"
                    :disabled="caseIsReadOnly || modalLoading"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Edit Continue Button Modal -->
        <base-modal id="edit-continue-modal" :centered="true">
            <template slot="title"> Edit Continue Button Label </template>
            <template>
                <base-form
                    v-model="selectedNode"
                    :fields="continueButtonFields"
                    :inline-style="true"
                    class="mt-3"
                ></base-form>
            </template>
            <template slot="footer">
                <button class="btn btn-primary" type="button" :disabled="saveNodeDisabled" @click="saveContinueBtn()">
                    Save
                </button>
            </template>
        </base-modal>
    </div>
</template>

<script>
import http from "@/axios.js"
import throttle from "lodash.throttle"
import BaseForm from "@/components/BaseForm"
import BaseModal from "@/components/BaseModal"
import NodeSelect from "@/components/NodeSelect"
import PanelEditor from "@/components/PanelEditor"
import NodeContentPreview from "@/components/NodeContentPreview"
import { continueRequirementOptions } from "@/assets/js/settings.js"

export default {
    name: "CaseNodeMap",
    components: {
        BaseForm,
        NodeMapBranch: () => import("@/components/NodeMapBranch"),
        BaseModal,
        NodeSelect,
        PanelEditor,
        NodeContentPreview,
    },
    data() {
        return {
            // Either 'tree' or 'grid' - controls how the map is rendered.
            mapViewType: null,
            // A list of SVG polylines connecting nodes in the map.
            connectionPolylines: [],
            // A list of connection points to be rendered where lines meet nodes.
            connectionStartMarkers: [],
            // A flag to control the existence of the SVG element in the DOM.
            svgRendered: false,
            // Controls whether or not the editor sidebar is shown.
            showEditor: false,
            // The node that's currently selected in the map.
            selectedNode: {
                id: null,
                columns: [],
            },
            // Controls the appearance of the loading spinner in the editor sidebar.
            editorLoading: false,
            // Controls the appearance of the save button loading spinner.
            saveLoading: false,
            // The list of form fields for editing a node's settings.
            editorFields: [
                {
                    name: "Title",
                    key: "title",
                    required: true,
                },
                {
                    name: "Completion Point",
                    key: "completion_node",
                    type: "checkbox",
                    required: false,
                    description: "Check this box if reaching this screen means the learner has completed the scenario.",
                },
                {
                    name: "End Screen",
                    key: "final_node",
                    type: "checkbox",
                    required: false,
                    description:
                        "Check this box if this is a final screen in the scenario. Learners will be directed back to the scenario library view.",
                },
                {
                    name: "Fail Screen",
                    key: "end_node",
                    type: "checkbox",
                    required: false,
                    description:
                        "Check this box if visiting this screen means the student has failed the scenario, and should try again from the beginning.",
                },
                {
                    name: "Show Continue Button",
                    key: "continue_button",
                    type: "checkbox",
                    required: false,
                    description: "Check this box if the continue button should be shown on this screen.",
                    editBtnText: "Label",
                },
            ],
            // The x-position of the left side boundary of the map.
            mapBoundaryLeft: null,
            // The x-position of the right side boundary of the map.
            mapBoundaryRight: null,
            // The allowable zoom levels when zooming in and out of the map.
            viewZoomOptions: [0.55, 0.70, 0.85, 1],
            // The index of the currently selected zoom option.
            currentZoomIndex: 2,
            // Base css style values to be scaled up by multiplying with the current zoom option.
            baseZoomNodeStyles: {
                width: 190,
                height: 96,
                "margin-left": 22,
                "margin-right": 22,
                "margin-bottom": 80,
                "font-size": 15,
                padding: 12,
            },
            // Base widths of SVG elements to be scaled up by multiplying with the current zoom option.
            baseArrowWidth: 9,
            baseLineWidth: 1.5,
            baseMarkerRadius: 2.65,
            // The window resize event handler, stored so that it can be removed when the map view is exited.
            resizeWindowHandler: null,
            modalLoading: false,
            // Set to true while switching between map view types.
            switchingView: false,
            // Form fields when creating a new node.
            newNodeFields: [
                {
                    name: "Title",
                    key: "title",
                    required: true,
                    placeholder: "Screen Title",
                },
            ],
            newNode: {},
            // Fields for editing the continue button.
            continueButtonFields: [
                {
                    name: "Button Label",
                    key: "continue_button_text",
                    maxlength: 50,
                    placeholder: "Continue",
                    subtext: "Add a custom continue button label for this screen.",
                    showLimit: true,
                },
            ],
            continueRequirementOptions: continueRequirementOptions,
        }
    },
    computed: {
        caseIsReadOnly() {
            return this.$store.getters.caseIsReadOnly
        },
        caseID() {
            return this.$route.params.id
        },
        caseObj() {
            return this.$store.getters.case
        },
        caseBaseUrl() {
            return this.$store.getters.caseBaseUrl
        },
        loading() {
            return this.$store.getters.loading
        },
        queryNodeID() {
            return this.$route.query.nodeID || null
        },
        nodeOptions() {
            let vm = this
            return this.$store.getters.nodeOptions.filter((n) => n.id != vm.selectedNode.id)
        },
        selectedNodeRoute() {
            return `${this.caseBaseUrl}/nodes/${this.selectedNode.id}/?scrollIntoView=true`
        },
        scrollToNavItem() {
            return this.$route.query.scrollToNavItem
        },
        currentZoomScale() {
            return this.viewZoomOptions[this.currentZoomIndex]
        },
        currentZoomNodeStyles() {
            let zoomStyles = {}
            for (const key in this.baseZoomNodeStyles) {
                zoomStyles[key] = this.baseZoomNodeStyles[key] * this.currentZoomScale
            }
            return zoomStyles
        },
        currentZoomNodeStylesFormatted() {
            let zoomStylesFormatted = {}
            for (const key in this.currentZoomNodeStyles) {
                zoomStylesFormatted[key] = this.currentZoomNodeStyles[key] + "px"
            }
            return zoomStylesFormatted
        },
        zoomedArrowWidth() {
            return this.baseArrowWidth * this.currentZoomScale
        },
        zoomedSelectedArrowWidth() {
            return this.zoomedArrowWidth * 1.2
        },
        zoomedLineWidth() {
            return this.baseLineWidth * this.currentZoomScale
        },
        zoomedSelectedLineWidth() {
            return this.zoomedLineWidth * 1.8
        },
        zoomedMarkerRadius() {
            return this.baseMarkerRadius * this.currentZoomScale
        },
        zoomedSelectedMarkerRadius() {
            return this.zoomedMarkerRadius * 1.3
        },
        increaseZoomDisabled() {
            return this.currentZoomIndex >= this.viewZoomOptions.length - 1
        },
        decreaseZoomDisabled() {
            return this.currentZoomIndex <= 0
        },
        storedMapViewType() {
            return this.$store.getters.mapViewType
        },
        nodeMap() {
            return this.$store.getters.caseNodeMap
        },
        nodeList() {
            return this.nodeMap["nodes"]
        },
        nodeLinks() {
            return this.nodeMap["node_links"]
        },
        mapSelectedNode() {
            return this.$store.getters.mapSelectedNode
        },
        saveNodeDisabled() {
            return this.editorLoading || this.saveLoading || this.caseIsReadOnly
        },
        selectedNodePanelsFlat() {
            let columns = this.selectedNode.columns || []
            return columns.reduce((acc, column) => acc.concat(column.panels), [])
        },
    },
    methods: {
        // Initialize the map view.
        async initializeMap() {
            await this.fetchData()
            if (this.queryNodeID) {
                // If there's a node specified in the query parameters, open the editor to view
                // the specified node and bring it into focus.
                let selectedNode = { id: this.queryNodeID }
                if (selectedNode) {
                    this.openNodeDetail(selectedNode, true)
                }
            }

            // Add an event listener to redraw the map on window resize.
            let vm = this
            let resize = throttle(function () {
                vm.drawConnections()
            }, 500)
            window.addEventListener("resize", resize)

            // Save the resize function so that we can remove the event listener on exit.
            this.resizeWindowHandler = resize
        },
        // Fetch data for the current view.
        async fetchData() {
            this.$store.dispatch("addLoad")
            let map = await this.$store.dispatch("fetchCaseNodeMap", {
                caseID: this.caseID,
                viewType: this.mapViewType,
            })
            this.switchingView = false
            this.$store.dispatch("removeLoad")

            let vm = this

            // Initialize the zoom based on the width of the map - wait for the map
            // blocks to render first.
            // eslint-disable-next-line
            await this.$nextTick(function () {
                if (window.innerWidth > 1600) {
                    vm.currentZoomIndex = 3
                } else {
                    vm.currentZoomIndex = 2
                }
            })

            // Draw connections after next render tick.
            this.$nextTick(function () {
                vm.drawConnections()

                // Using this flag to control the existence of the SVG element in the DOM.
                // This seems to fix a bug in Safari where the SVG lines disappear after being drawn.
                vm.svgRendered = true

                let selectedNodeID = null
                if (vm.selectedNode.id) {
                    selectedNodeID = vm.selectedNode.id
                    vm.focusNode({ id: selectedNodeID })
                } else if (vm.queryNodeID) {
                    selectedNodeID = vm.queryNodeID
                    vm.focusNode({ id: selectedNodeID })
                } else if (vm.mapViewType == "tree") {
                    // Select the first node of the tree-style map.
                    selectedNodeID = map.nodes[0]["id"]
                } else {
                    // Select the first node of the grid-style map.
                    selectedNodeID = map.nodes[0][0]["id"]
                }

                // Scroll to the node.
                vm.$nextTick(function () {
                    vm.scrollToNodeByID(selectedNodeID, true)
                })
            })
        },
        // Fetch details about a given node.
        async fetchNode(nodeObj) {
            let vm = this
            return http
                .get("/api/nodes/" + nodeObj.id + "/map/")
                .then(function (response) {
                    vm.selectedNode = response.data
                })
                .finally(function () {
                    vm.editorLoading = false
                })
        },
        // Find a reference to a node within the map structure.
        fetchNodeById(nodeId) {
            let selectedNode = null
            for (let nodeLevel of this.nodeList) {
                for (let node of nodeLevel) {
                    if (node.id == nodeId) {
                        selectedNode = node
                        break
                    }
                    if (selectedNode) break
                }
            }
            return selectedNode
        },
        // Get a node DOM element, by the node's ID.
        getNodeElementByID(nodeID) {
            return document.getElementById("map-node-" + nodeID)
        },
        // Get the left and right boundaries of the map.
        getMapBoundaries() {
            let mapContainer = null
            if (this.mapViewType == "tree") {
                mapContainer = document.getElementById("tree-container")
            } else {
                mapContainer = document.getElementById("grid-container")
            }
            // Get the x position of the middle of the map container.
            let mapOffsetLeft = mapContainer.offsetLeft
            let mapWidth = mapContainer.offsetWidth

            this.mapBoundaryLeft = mapOffsetLeft
            this.mapBoundaryRight = mapOffsetLeft + mapWidth
        },
        // Draw connections between the node blocks on the map.
        drawConnections() {
            this.getMapBoundaries()
            // Clear any existing polylines and markers.
            this.connectionPolylines = []
            this.connectionStartMarkers = []
            let vm = this
            this.nodeLinks.forEach((nodeLink) => {
                vm.createConnector(nodeLink)
            })
        },
        // Return the positions of all the faces of the node element, and the position of its center.
        getNodeWithPosition(nodeID) {
            let nodeElement = this.getNodeElementByID(nodeID)
            let svgContainerElement = document.getElementById("map-svg-container")

            // If either elements arent rendered yet, return null.
            if (!nodeElement || !svgContainerElement) {
                return null
            }
            // Calculate y position of the given node, relative to the svg container.
            let svgContainerOffsetTop = svgContainerElement.offsetTop
            let selectedNodeOffsetTop = nodeElement.offsetTop
            let selectedNodeTopPostionRelative = selectedNodeOffsetTop - svgContainerOffsetTop

            // Calculate the x position.
            let svgContainerOffsetLeft = svgContainerElement.offsetLeft
            let selectedNodeOffsetLeft = nodeElement.offsetLeft
            let selectedNodeLeftPostionRelative = selectedNodeOffsetLeft - svgContainerOffsetLeft

            // Get the size of the element.
            let width = nodeElement.offsetWidth
            let height = nodeElement.offsetHeight

            return {
                left: {
                    x: selectedNodeLeftPostionRelative,
                    y: selectedNodeTopPostionRelative + height / 2,
                },
                top: {
                    x: selectedNodeLeftPostionRelative + width / 2,
                    y: selectedNodeTopPostionRelative,
                },
                right: {
                    x: selectedNodeLeftPostionRelative + width,
                    y: selectedNodeTopPostionRelative + height / 2,
                },
                bottom: {
                    x: selectedNodeLeftPostionRelative + width / 2,
                    y: selectedNodeTopPostionRelative + height,
                },
                middle: {
                    x: selectedNodeLeftPostionRelative + width / 2,
                    y: selectedNodeTopPostionRelative + height / 2,
                },
            }
        },
        // Create a polyline between two nodes, with special rules to determine
        // direction and layout.
        createConnector(nodeLink) {
            let startNodeId = nodeLink.link[0]
            let endNodeId = nodeLink.link[1]

            // Get initial coordinates for both nodes.
            let startNode = this.getNodeWithPosition(startNodeId)
            let endNode = this.getNodeWithPosition(endNodeId)

            if (!startNode || !endNode) {
                return
            }

            // Do some math to set some info about the relative positons of the two nodes in the map.
            let startAboveEnd = endNode.middle.y > startNode.middle.y
            let startBelowEnd = endNode.middle.y < startNode.middle.y

            let horizontalDifference = Math.abs(startNode.middle.x - endNode.middle.x)
            let verticalDifference = Math.abs(startNode.middle.y - endNode.middle.y)

            let isHorizontallyClose = false
            if (horizontalDifference < 300) {
                isHorizontallyClose = true
            }

            let isVerticallyFar = false
            if (verticalDifference > 700) {
                isVerticallyFar = true
            }

            // Initialize the connector line.
            let connector = {
                start: {},
                end: {},
                surfaceOrientation: "horizontal",
                straightLine: false,
                loopback: startBelowEnd,
            }

            // A constant that offsets the X position of the contact point
            // of loopback lines.
            const loopbackXOffset = (this.currentZoomScale * 96) / 2
            // A constant that spaces out connections so they don't all intersect.
            const minorOffset = 12

            // Determine which face of the nodes to start and end at.
            if (startAboveEnd) {
                // Normal flow - start node is above the end node.
                // Start at the bottom face of the start node.
                connector.start = startNode.bottom
                connector.end = endNode.top
            } else if (startBelowEnd) {
                // Looping back - start node is below the end node.
                if (startNode.middle.x > endNode.middle.x && isVerticallyFar) {
                    // Bow around the map for long loopbacks - connect left side to left side.
                    connector.start = startNode.right
                    connector.start.y -= minorOffset
                    connector.end = endNode.right
                    connector.end.y += minorOffset
                    connector.surfaceOrientation = "vertical"
                } else if (startNode.middle.x <= endNode.middle.x && isVerticallyFar) {
                    // Bow around the map for long loopbacks - connect right side to right side.
                    connector.start = startNode.left
                    connector.start.y -= minorOffset
                    connector.end = endNode.left
                    connector.end.y += minorOffset
                    connector.surfaceOrientation = "vertical"
                } else if (startNode.left.x > endNode.right.x && isHorizontallyClose) {
                    // Connect right side of start to left side of end.
                    connector.start = startNode.left
                    connector.start.y -= minorOffset
                    connector.end = endNode.right
                    connector.end.y += minorOffset
                    connector.surfaceOrientation = "vertical"
                } else if (endNode.left.x > startNode.right.x && isHorizontallyClose) {
                    // Connect left side of start to the right side of end.
                    connector.start = startNode.right
                    connector.start.y -= minorOffset
                    connector.end = endNode.left
                    connector.end.y += minorOffset
                    connector.surfaceOrientation = "vertical"
                } else if (startNode.left.x > endNode.right.x) {
                    // Connect right side of start to left side of end.
                    connector.start = startNode.left
                    connector.end = endNode.bottom
                    connector.end.x += loopbackXOffset
                    connector.surfaceOrientation = "mixed"
                } else if (endNode.left.x > startNode.right.x) {
                    // Connect left side of start to the right side of end.
                    connector.start = startNode.right
                    connector.end = endNode.bottom
                    connector.end.x -= loopbackXOffset
                    connector.surfaceOrientation = "mixed"
                } else if (startNode.middle.x > endNode.middle.x) {
                    // Nodes overlap horizontally, so just connect with a straight line, offset to the left.
                    connector.start = { x: startNode.top.x - loopbackXOffset, y: startNode.top.y }
                    connector.end = { x: startNode.top.x - loopbackXOffset, y: endNode.bottom.y }
                    connector.straightLine = true
                } else {
                    // Nodes overlap horizontally, so just connect with a straight line, offset to the right.
                    connector.start = { x: startNode.top.x + loopbackXOffset, y: startNode.top.y }
                    connector.end = { x: startNode.top.x + loopbackXOffset, y: endNode.bottom.y }
                    connector.straightLine = true
                }
            } else {
                // Connecting nodes that are on the same horizontal plane - just use a straight line.
                connector.surfaceOrientation = "vertical"
                if (startNode.middle.x > endNode.middle.x) {
                    connector.start = startNode.left
                    connector.end = endNode.right
                } else {
                    connector.start = startNode.right
                    connector.end = endNode.left
                }
            }

            // Initialize the polyline object, with a path string set to a straight line between the two
            // points, no styling.
            let polyline = {
                startNodeId: startNodeId,
                endNodeId: endNodeId,
                type: nodeLink.type,
                focus: false,
                hoverFocus: false,
                loopBack: false,
                dashedArr: "",
                class: "",
                arrow: "url(#arrow-loopback)",
                str: `${connector.start.x},${connector.start.y} ${connector.end.x},${connector.end.y}`,
            }

            // Define the polyline itself.
            if (connector.straightLine) {
                polyline.str = `${connector.start.x},${connector.start.y} ${connector.end.x},${connector.end.y}`
            } else if (connector.loopback && isVerticallyFar) {
                // Connect to a node that's really far up the map.
                let mapSideXPosition = this.mapBoundaryRight
                if (connector.start.x <= connector.end.x) {
                    mapSideXPosition = this.mapBoundaryLeft
                }
                polyline.str = `${connector.start.x},${connector.start.y} ${mapSideXPosition},${connector.start.y} ${mapSideXPosition},${connector.end.y} ${connector.end.x},${connector.end.y}`
            } else if (connector.surfaceOrientation == "horizontal") {
                // Connect horizontally oriented faces.
                let halfwayYPosition = (connector.end.y - connector.start.y) / 2 + connector.start.y
                if (connector.end.y < connector.start.y) {
                    halfwayYPosition = (connector.start.y - connector.end.y) / 2 + connector.end.y
                }
                polyline.str = `${connector.start.x},${connector.start.y} ${connector.start.x},${halfwayYPosition} ${connector.end.x},${halfwayYPosition} ${connector.end.x},${connector.end.y}`
            } else if (connector.surfaceOrientation == "vertical") {
                // Connect vertically oriented faces.
                let halfwayXPosition = (connector.end.x - connector.start.x) / 2 + connector.start.x
                if (connector.end.x < connector.start.x) {
                    halfwayXPosition = (connector.start.x - connector.end.x) / 2 + connector.end.x
                }
                polyline.str = `${connector.start.x},${connector.start.y} ${halfwayXPosition},${connector.start.y} ${halfwayXPosition},${connector.end.y} ${connector.end.x},${connector.end.y}`
            } else {
                // Connect with a single elbow line.
                polyline.str = `${connector.start.x},${connector.start.y} ${connector.end.x},${connector.start.y} ${connector.end.x},${connector.end.y}`
            }

            // Set the line arrowhead type.
            if (startAboveEnd) {
                polyline.arrow = "url(#arrow-forward)"
            } else {
                polyline.arrow = "url(#arrow-loopback)"
            }

            // Set the polyline styling based on whether it's a loop-back or not.
            if (!startAboveEnd) {
                polyline.dashedArr = "4,4"
                polyline.loopBack = true
            }

            // Append the polyline to the array of polylines.
            this.connectionPolylines.push(polyline)

            // Append the dot marking the start of the polyline.
            this.connectionStartMarkers.push({
                nodeId: startNodeId,
                x: connector.start.x,
                y: connector.start.y,
                loopback: !startAboveEnd,
                focus: false,
                hoverFocus: false,
            })
        },
        // Bring a node into focussed or hover-focussed state.
        focusNode(nodeObj, hover = false) {
            let connectorLines = this.connectionPolylines.filter((line) => line.startNodeId == nodeObj.id)
            connectorLines.forEach((line) => {
                if (hover) {
                    line.hoverFocus = true
                } else {
                    line.focus = true
                    // This is a hack - you can't set the z-index of svg elements, to in order
                    // to place the focussed lines on top, I'm ripping them out of the DOM and
                    // appending them back into the svg container. Waiting for nexTick is also a hack here.
                    this.$nextTick(function () {
                        let element = document.getElementById("line-" + line.startNodeId + "to" + line.endNodeId)
                        element.remove()
                        let container = document.getElementById("map-svg")
                        container.append(element)
                    })
                }
            })
            let connectionMarkers = this.connectionStartMarkers.filter((marker) => marker.nodeId == nodeObj.id)
            connectionMarkers.forEach((marker) => {
                if (hover) {
                    marker.hoverFocus = true
                } else {
                    marker.focus = true
                }
            })
        },
        // Remove the focussed state from all nodes.
        unfocusNodes() {
            this.connectionPolylines.forEach((line) => (line.focus = false))
            this.connectionStartMarkers.forEach((marker) => (marker.focus = false))
        },
        // Handle a mouseover event on a node.
        mouseOverNode(nodeObj) {
            this.focusNode(nodeObj, true)
        },
        // Handle a mouse-leave event on a node.
        mouseLeaveNode(nodeObj) {
            this.connectionPolylines.forEach((line) => (line.hoverFocus = false))
            this.connectionStartMarkers.forEach((marker) => (marker.hoverFocus = false))
        },
        // Select a node and open the node editor panel.
        openNodeDetail(nodeObj, scrollIntoView = false) {
            this.$refs.nodeSelector.reset()
            this.$store.commit("setMapSelectedNode", nodeObj)
            this.unfocusNodes()
            this.showEditor = true
            this.editorLoading = true
            let nodeElement = this.getNodeElementByID(nodeObj.id)
            if (scrollIntoView) {
                nodeElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                    inline: "center",
                })
            }
            this.fetchNode(nodeObj)
            this.focusNode(nodeObj)
        },
        // Close the node editor panel.
        closeEditor(event = null) {
            this.$store.commit("clearMapSelectedNode")
            this.clearSelectedNode()
            this.unfocusNodes()
            if (!event) {
                this.showEditor = false
                return
            }
            // Find out whether the click originated from outside the editor menu.
            let clickedInMenu = Boolean(event.target.closest("#editor-menu"))
            if (this.showEditor && !clickedInMenu) {
                this.showEditor = false
            }
        },
        // Save the currently selected node.
        saveNode(showNotification = true) {
            this.editorLoading = true
            this.saveLoading = true
            let viewType = this.mapViewType
            // Hack to fix blank custom continue requirement.
            if (!this.selectedNode.correct_answers_required_num) {
                this.selectedNode.correct_answers_required_num = 0
            }
            let node = Object.assign({}, this.selectedNode)
            delete node.panels
            let vm = this
            return new Promise((resolve) => {
                http.put("/api/nodes/" + this.selectedNode.id + "/map/?viewType=" + viewType, node)
                    .then(function (response) {
                        // Structure has changed, so both maps need an update.
                        vm.$store.commit("mapRequiresUpdate")
                        // Set the new map structure.
                        vm.$store.commit("setCaseNodeMap", {
                            map: response.data.map,
                            viewType: viewType,
                        })

                        // Only update the selected node if the user is still viewing it (they may have)
                        // clicked on something else.
                        if (vm.selectedNode.id == response.data.node.id) {
                            vm.selectedNode = response.data.node
                        }

                        // Set the new list of nodes in the datastore.
                        vm.$store.commit("setNodeList", response.data.nodes)
                        vm.$store.commit("selectedNodeRequiresUpdate")

                        if (showNotification) {
                            vm.$notify({
                                group: "main",
                                type: "success",
                                text: "Screen updated.",
                                data: {},
                            })
                        }

                        // Re-render the map.
                        vm.$nextTick(function () {
                            vm.svgRendered = false
                            vm.drawConnections()
                            vm.svgRendered = true
                            vm.focusNode(vm.selectedNode)
                        })
                    })
                    .finally(function () {
                        vm.editorLoading = false
                        vm.saveLoading = false
                        resolve()
                    })
            })
        },
        // Save continue button settings.
        saveContinueBtn() {
            $("#edit-continue-modal").modal("hide")
            this.saveNode()
        },
        // Open the delete node modal.
        openDeleteNode() {
            $("#delete-node-modal").modal("show")
        },
        // Delete the selected node.
        deleteNode() {
            this.modalLoading = true
            let viewType = this.mapViewType
            let vm = this
            http.put("/api/nodes/" + this.selectedNode.id + "/map/remove/?viewType=" + viewType)
                .then(function (response) {
                    $("#delete-node-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Screen deleted.",
                        data: {},
                    })

                    // Structure has changed, so both maps need an update.
                    vm.$store.commit("mapRequiresUpdate")
                    // Set the new map structure.
                    vm.$store.commit("setCaseNodeMap", {
                        map: response.data.map,
                        viewType: viewType,
                    })

                    vm.closeEditor()
                    // Set the new list of nodes in the datastore.
                    vm.$store.commit("setNodeList", response.data.nodes)
                    vm.$store.commit("selectedNodeRequiresUpdate")
                    // Re-render the map.
                    vm.$nextTick(function () {
                        vm.svgRendered = false
                        vm.drawConnections()
                        vm.svgRendered = true
                    })
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Increase the zoom of the current view.
        increaseZoom() {
            if (this.increaseZoomDisabled) {
                return
            }
            this.currentZoomIndex++
            let vm = this
            this.$nextTick(function () {
                vm.drawConnections()
                // Refocus the node if one is currently selected.
                if (vm.selectedNode.id) {
                    vm.focusNode(vm.selectedNode)
                    vm.scrollToNodeByID(vm.selectedNode.id)
                }
            })
        },
        // Decrease the zoom of the current view.
        decreaseZoom() {
            if (this.decreaseZoomDisabled) {
                return
            }
            this.currentZoomIndex--
            let vm = this
            this.$nextTick(function () {
                vm.drawConnections()
                // Refocus the node if one is currently selected.
                if (vm.selectedNode.id) {
                    vm.focusNode(vm.selectedNode)
                    vm.scrollToNodeByID(vm.selectedNode.id)
                }
            })
        },
        // Scroll to a node in the map with a given ID.
        scrollToNodeByID(nodeID, smooth = false) {
            let nodeElement = this.getNodeElementByID(nodeID)
            let scrollOptions = {
                block: "center",
                inline: "center",
            }
            if (smooth) {
                scrollOptions["behavior"] = "smooth"
            }
            nodeElement.scrollIntoView(scrollOptions)
        },
        // Open the map view.
        openMapView(viewType) {
            if (this.loading) {
                return
            }
            this.switchingView = true
            this.mapViewType = viewType
            this.fetchData()
        },
        // Get the width to display a line, based on its state.
        getZoomedLineWidth(polyline) {
            if (polyline.focus || polyline.hoverFocus) {
                return this.zoomedSelectedLineWidth
            }
            return this.zoomedLineWidth
        },
        // Get the radius for a given marker, based on its state.
        getZoomedMarkerRadius(marker) {
            if (marker.focus || marker.hoverFocus) {
                return this.zoomedSelectedMarkerRadius
            }
            return this.zoomedMarkerRadius
        },
        // Clear the currently selected node.
        clearSelectedNode() {
            this.selectedNode = {
                id: null,
                columns: [],
            }
        },
        // Launch a case session to preview the current node.
        previewNode() {
            this.$store.dispatch("clearSession")
            this.$store.dispatch("addLoad")
            let vm = this
            http.post("/api/cases/" + vm.caseID + "/preview/start/", {
                startNodeID: this.selectedNode.id,
            }).then(function (response) {
                vm.$store.dispatch("removeLoad")
                vm.$router.push(`${vm.caseBaseUrl}/preview/${response.data.session_id}/?fromView=map`)
            })
        },
        // Open the edit panel modal and fetch the panel data.
        openEditPanel(panel) {
            let panelCopy = Object.assign({}, panel)
            this.$refs.panelEditor.open(panelCopy)
        },
        // A callback used when a panel has been updated.
        panelUpdatedCb() {
            // For now, just save the whole node in order to fetch the updated map structure.
            // In the future, this should be changed to its own handler.
            this.saveNode(false)
        },
        // Open the create node modal.
        openCreateNode() {
            this.$refs.createNodeForm.resetValidate()
            // Initialize the node object for creation.
            this.newNode = {}
            $("#create-node-modal").modal("show")
        },
        // Create a new node.
        createNode() {
            // Check form validity.
            if (!this.$refs.createNodeForm.validate()) {
                return
            }
            this.modalLoading = true
            let vm = this
            http.post("/api/cases/" + this.caseID + "/nodes/", this.newNode)
                .then(function (response) {
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Screen created.",
                        data: {},
                    })

                    // Format the new node to work with the map view.
                    let newNode = {
                        ...response.data,
                        panels: [],
                    }
                    // Append the new node to map structure. This prevents us from having to
                    // make a new call to fetch the map structure again.
                    vm.$store.commit("appendFloatingNodeToMap", Object.assign({}, newNode))
                    // Refresh the list of nodes.
                    vm.$store.dispatch("refreshCase", vm.caseID)

                    // Bring the new node into focus after the next re-render.
                    vm.$nextTick(function () {
                        vm.openNodeDetail(newNode, true)
                    })

                    $("#create-node-modal").modal("hide")
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Handle the edit button beside the continue button field being pressed.
        nodeFormEditCb(field) {
            if (field == "continue_button") {
                $("#edit-continue-modal").modal("show")
            }
        },
    },
    mounted() {
        if (this.scrollToNavItem) {
            // Show the map nav item in the sidebar.
            let mapNavigationElement = document.getElementById("sidenav-map-link")
            this.$nextTick(function () {
                mapNavigationElement.scrollIntoView({
                    // Removing the scroll effect for now, feels chaotic with too much scrolling.
                    // behavior: "smooth",
                    block: "center",
                })
            })
        }
        // Use the same map view as was previously viewed.
        this.mapViewType = this.storedMapViewType
        this.initializeMap()
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.resizeWindowHandler)
    },
    // Called by the router before navigating away from this component.
    beforeRouteLeave(to, from, next) {
        this.$refs.panelEditor.closeEditModals()
        next()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/nodemap.scss";
@import "@/assets/styles/paneledit.scss";

.map-parent {
    padding-left: 110px;
    padding-right: 90px;
}

.map-container {
    min-height: 90vh;
}

.svg-container {
    height: 100%;
    width: 100%;
    position: absolute;
    overflow: visible;
    pointer-events: none;
}

.line-container {
    z-index: 10;
}

.point-container {
    z-index: 90;
}

.svg-parent {
    height: 100%;
    width: 100%;
    overflow: visible;
}

.svg-line {
    fill: none;
    transition: stroke 0.1s ease-in-out, stroke-width 0.1s ease-in-out, opacity 0.1s ease-in-out;
}

.svg-forward {
    stroke: $forward;
    &.svg-element-faded {
        stroke: lighten($forward, $lighten);
    }
}

.svg-loopback {
    stroke: $node-connector-color;
    &.svg-element-faded {
        stroke: lighten($node-connector-color, $lighten);
    }
}

.svg-arrow-forward {
    fill: $forward;
    &.svg-element-faded {
        fill: lighten($forward, $lighten);
    }
}

.svg-arrow-selected {
    fill: $selected !important;
}

.svg-arrow-loopback {
    fill: $loopback;
    &.svg-element-faded {
        fill: lighten($loopback, $lighten);
    }
}

.svg-line-selected {
    stroke: $selected !important;
    opacity: 1 !important;
}

.svg-connection-point {
    transition: fill 0.1s ease-in-out, r 0.1s ease-in-out, opacity 0.1s ease-in-out;
}

.svg-connection-point-loopback {
    fill: $loopback;
    &.svg-element-faded {
        fill: lighten($loopback, $lighten);
    }
}

.svg-connection-point-forward {
    fill: $forward;
    &.svg-element-faded {
        fill: lighten($forward, $lighten);
    }
}

.svg-connection-point-focus {
    fill: $selected !important;
    opacity: 1 !important;
}

.left-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1030;
    width: 375px;
    height: 100%;
    overflow-y: scroll;
    outline: 0;
}

.left-sidebar-content {
    margin-left: 0px;
    margin-top: 0px;
    margin-bottom: 0px;
    height: 100%;
}

.left-sidebar-content-block {
    padding-top: 16px;
    padding-bottom: 16px;
    padding-left: 14px;
    padding-right: 14px;
}

.node-label {
    color: $gray-700;
}

.inquiry-block {
    background-color: $gray-50;
    padding-bottom: 2px;
    padding-top: 12px;
    padding-right: 10px;
    padding-left: 10px;
}

.zoom-btns {
    z-index: 100;
    right: 0px;
}

.tool-btn {
    width: 40px;
}

.panel-container {
    padding-top: 14px;
    padding-bottom: 2px;
    padding-left: 4px;
    padding-right: 4px;
}

.badge-requirement {
    text-align: left;
    white-space: pre-wrap;
    line-height: 1.2;
}

.node-preview-container {
    min-height: 100px;
}
</style>